import {Icon, Spinner, SpinnerSize, Tab} from "@blueprintjs/core";
import { useApiEffect } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { TitlesRequestApi, RequestApi, ERequestType, ERequestState } from "../../../../api";
import { ERoutes } from "../../../../ERoutes";
import {
  BackButton,
  PageBase,
  RequestStickyMenu,
  ProtectedTabs,
  VerificationTab,
  RequestHistoryTab,
  LinkedSubFilesTab,
  ConditionTab,
} from "../../../../components";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { TitleRequestDetails } from "./TitleRequestDetails";
import { useGlobalData } from "../../../../contexts";
import { BillTab } from "../../BillTab";
import { MailLogListPage } from "../../../mail-log/MailLogListPage";

export interface ITitleRequestItemPageProps {}
export const TitleRequestItemPage: React.FunctionComponent<ITitleRequestItemPageProps> = (props) => {
  const {
    id,
    tab,
    state,
    masterRequestId = 0,
  } = useParams<{
    id: string;
    tab: string;
    state: string;
    masterRequestId: string;
  }>();
  const history = useHistory();
  const api = useApiService(TitlesRequestApi);
  const requestApi = useApiService(RequestApi);
  const { currentLanguage } = useGlobalData();

  const [requestInfo, requestInfoLoading, , refreshRequestInfo] = useApiEffect(
    () => requestApi.getRequestInfo(ERequestType.TitleRequest, +id, +masterRequestId, currentLanguage.languageId),
    [requestApi, id, masterRequestId, currentLanguage],
  );

  const refreshRequest = React.useCallback(() => {
    refreshRequestInfo();
  }, [refreshRequestInfo]);

  const closeRequest = React.useCallback(async () => {
    await api.close(requestInfo?.parentRequestId);
  }, [api, requestInfo]);

  const { t } = useTl();

  return (
    <PageBase
      breadCrumbs={[
        {
          text: t(ETLCodes.TitleRequests),
          route: ERoutes.titleRequests,
        },
        {
          text:
            +id > 0 ? (
              requestInfoLoading ? (
                <Spinner size={SpinnerSize.SMALL} />
              ) : (
                requestInfo.requestNumber
              )
            ) : (
              t(ETLCodes.New)
            ),
        },
      ]}
      actionHistory={
        +id > 0 &&
        !requestInfoLoading && {
          tlCode: ETLCodes.ActionHistoryTitleRequest,
          name: requestInfo.requestNumber,
          id: +id,
        }
      }
      stickyContent={() => {
        if (+id > 0 && !requestInfoLoading) {
          return (
            <RequestStickyMenu
              requestId={requestInfo.parentRequestId}
              refreshFunc={refreshRequest}
              closeFunc={closeRequest}
            />
          );
        } else {
          return null;
        }
      }}
    >
      <ProtectedTabs
        id="TabsTitleRequest"
        onChange={(newTabId) => history.push(`${ERoutes.titleRequests}/${+id}/${newTabId}/${state}`)}
        selectedTabId={tab}
        renderActiveTabPanelOnly
      >
        <BackButton backRoute={ERoutes.titleRequests} />
        <Tab
          id="detail"
          title={
            <div>
              {t(ETLCodes.Detail)} {requestInfo?.hasWarning && <Icon icon="warning-sign" intent="danger" />}
            </div>
          }
          panel={<TitleRequestDetails titleRequestId={+id} editMode={state === "edit"} />}
        />
        <Tab
          id="verifications"
          title={t(ETLCodes.Verifications)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <VerificationTab
                requestId={requestInfo.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.VerificationsToObtainBilledStatus}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="conditions"
          title={t(ETLCodes.Conditions)}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <ConditionTab
                requestId={requestInfo?.parentRequestId}
                editMode={state === "edit"}
                statusETLCode={ETLCodes.ConditionsForBillingStatus}
                requestState={requestInfo?.requestStateCode}
              />
            )
          }
          disabled={+id <= 0}
        />
        <Tab
          id="subrequests"
          title={t(ETLCodes.SubRequests, {
            count: requestInfoLoading ? `` : `(${requestInfo.subRequestCount})`,
          })}
          panel={
            requestInfoLoading ? (
              <Spinner />
            ) : (
              <LinkedSubFilesTab requestInfo={requestInfo} editMode={state === "edit"} />
            )
          }
          disabled
        />
        <Tab
          id="billing"
          title={t(ETLCodes.Billing)}
          panel={
            <BillTab
              requestId={requestInfo?.parentRequestId}
              disabled={requestInfo?.requestStateCode !== ERequestState.CANCELLED}
            />
          }
          disabled={requestInfo?.requestStateCode === ERequestState.REQUESTED}
        />
        <Tab
          id="history"
          title={t(ETLCodes.History)}
          panel={requestInfoLoading ? <Spinner /> : <RequestHistoryTab requestId={requestInfo.parentRequestId} />}
          disabled={+id <= 0}
        />
        <Tab
          id="mailing"
          title={t(ETLCodes.Email)}
          panel={requestInfoLoading ? <Spinner /> : <MailLogListPage requestId={requestInfo?.parentRequestId} />}
          disabled={+id <= 0}
        />
      </ProtectedTabs>
    </PageBase>
  );
};
